import React from 'react';
import { useSiteData } from '../context/SiteDataContext';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const { siteData } = useSiteData();
  const { language, t } = useLanguage();
  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[80vh] md:min-h-[70vh] flex flex-col justify-center items-center bg-gradient-hero font-sans pt-header">
      {/* Hero Badge */}
      <div className="absolute top-8 md:top-10 left-1/2 -translate-x-1/2 z-10 px-4">
        <span className="inline-block bg-white/95 text-blue-700 font-bold text-xs md:text-sm lg:text-base px-4 md:px-6 py-2 rounded-full shadow-lg border-2 border-blue-500 tracking-wide uppercase backdrop-blur-sm">
          ⭐ Türkiye'nin Güvenilir Hasar Onarım Uzmanı
        </span>
      </div>
      
      {/* Animated Background */}
      <div className="absolute inset-0 gradient-hero">
        <div className="absolute inset-0 bg-black/20"></div>
        {/* Floating elements - hide on mobile for better performance */}
        <div className="hidden md:block absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
        <div className="hidden md:block absolute top-40 right-20 w-16 h-16 bg-white/10 rounded-full animate-float" style={{animationDelay: '1s'}}></div>
        <div className="hidden md:block absolute bottom-40 left-20 w-12 h-12 bg-white/10 rounded-full animate-float" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 flex flex-col items-center justify-center text-center relative z-20">
        <div className="h-16 md:h-14" /> {/* Badge ile başlık arasında boşluk - mobilde daha fazla */}
        <h1 className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-white mb-4 md:mb-6 drop-shadow-lg leading-tight">
          <span>{t('hero.title1')}</span>
          <br />
          <span className="text-yellow-400">{t('hero.title2')}</span>
        </h1>
        <p className="text-lg md:text-xl lg:text-2xl text-white/90 font-medium mb-8 md:mb-10 max-w-2xl mx-auto tracking-normal leading-relaxed">
          {siteData.hero.subtitle[language]}
        </p>
        
        {/* CTA Buttons - Improved mobile layout */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 justify-center items-center w-full max-w-4xl mb-8 md:mb-8 px-2">
          <button
            onClick={scrollToServices}
            className="group bg-white text-orange-600 px-6 py-3 md:px-8 md:py-4 lg:px-10 lg:py-5 rounded-2xl font-bold text-sm md:text-base lg:text-lg shadow-2xl hover:shadow-white/25 transform hover:-translate-y-2 transition-all duration-300 flex items-center w-full sm:w-auto justify-center min-h-[48px] focus:outline-none focus:ring-4 focus:ring-white/50"
          >
            {t('hero.explore')}
            <svg className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          
          <a
            href={`tel:${siteData.contactInfo.phone}`}
            className="group bg-transparent border-2 border-white text-white px-6 py-3 md:px-8 md:py-4 lg:px-10 lg:py-5 rounded-2xl font-bold text-sm md:text-base lg:text-lg hover:bg-white hover:text-orange-600 transition-all duration-300 flex items-center w-full sm:w-auto justify-center min-h-[48px] focus:outline-none focus:ring-4 focus:ring-white/50"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 mr-2 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span className="hidden sm:inline">{t('hero.emergency')}</span>
            <span className="sm:hidden">{t('hero.call')}</span>
          </a>

          <button
            onClick={() => {
              const phoneNumber = '905372959612';
              const message = encodeURIComponent(t('whatsapp.default'));
              const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
              window.open(whatsappUrl, '_blank');
            }}
            className="group bg-green-500 text-white px-6 py-3 md:px-8 md:py-4 lg:px-10 lg:py-5 rounded-2xl font-bold text-sm md:text-base lg:text-lg shadow-2xl hover:shadow-green-500/25 transform hover:-translate-y-2 transition-all duration-300 flex items-center whatsapp-pulse w-full sm:w-auto justify-center min-h-[48px] focus:outline-none focus:ring-4 focus:ring-green-500/50"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
            <span className="hidden sm:inline">WhatsApp</span>
            <span className="sm:hidden">{t('hero.message')}</span>
          </button>
        </div>

        {/* Stats Grid - Improved mobile layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-6 md:mt-8 w-full max-w-4xl px-2">
          <div className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-4 shadow-md hover:bg-white/15 transition-colors">
            <span className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-yellow-300 mb-1 md:mb-2 tracking-tight">10+</span>
            <span className="text-white/90 text-xs md:text-sm lg:text-base font-semibold tracking-wide text-center leading-tight">Yıllık Deneyim</span>
          </div>
          <div className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-4 shadow-md hover:bg-white/15 transition-colors">
            <span className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-yellow-300 mb-1 md:mb-2 tracking-tight">1000+</span>
            <span className="text-white/90 text-xs md:text-sm lg:text-base font-semibold tracking-wide text-center leading-tight">Başarılı Proje</span>
          </div>
          <div className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-4 shadow-md hover:bg-white/15 transition-colors">
            <span className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-yellow-300 mb-1 md:mb-2 tracking-tight">7/24</span>
            <span className="text-white/90 text-xs md:text-sm lg:text-base font-semibold tracking-wide text-center leading-tight">Acil Müdahale</span>
          </div>
          <div className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-4 shadow-md hover:bg-white/15 transition-colors">
            <span className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-yellow-300 mb-1 md:mb-2 tracking-tight">99%</span>
            <span className="text-white/90 text-xs md:text-sm lg:text-base font-semibold tracking-wide text-center leading-tight">Müşteri Memnuniyeti</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
